/** @format */

import { AxiosResponse } from "axios";
import { IValidationResult } from "lobbie";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { useHistory } from "react-router-dom";
import { useAxiosPublicPost } from "src/hooks/useAxios";
import * as yup from "yup";
import { API_PREFIX } from "src/constants";
import { handleError, notify } from "src/utils";
import LobbieSpinner from "../loading/LobbieSpinner";

const VALIDATION_SCHEMA = yup.object().shape({
    email: yup.string().required().email(),
});

const ForgotPassword: React.FC = () => {
    const {
        goBack,
        location: { state },
    } = useHistory() as Record<string, any>;
    const poster = useAxiosPublicPost();
    const [isSubmitting, setSubmitting] = useState<boolean>(false);

    const id = useMemo(() => state?.id as number, [state]);
    const historyStateEmail = useMemo(() => state?.email as string, [state]);
    const [email, setEmail] = useState<string>(historyStateEmail || "");

    useEffect(() => {
        setEmail(historyStateEmail || "");
    }, [historyStateEmail]);

    const onSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            e.stopPropagation();

            try {
                VALIDATION_SCHEMA.validateSync({ email });
            } catch (error) {
                notify({
                    level: "warning",
                    title: "Invalid email",
                    message: "Please enter a valid email address.",
                });
                return;
            }

            const data = { email };
            if (id) {
                data["id"] = id;
            }
            setSubmitting(true);
            await poster(`${API_PREFIX}/forgotPassword/create`, data)
                .then((response: AxiosResponse | void) => {
                    setSubmitting(false);
                    if (!response) {
                        notify({
                            level: "error",
                            title: "Error resetting password.",
                            message: "Please re-enter your email and try again.",
                        });
                        return;
                    }
                    const { success, message } = response.data as IValidationResult;
                    if (!success) {
                        notify({
                            level: "error",
                            title: "Failed to reset password.",
                            message: message || "Please re-enter your email and try again.",
                        });
                        return;
                    }

                    notify({
                        level: "success",
                        title: "Success",
                        message:
                            "Password reset email sent, it should be there in a minute. Try checking your spam folder as well.",
                    });
                })
                .catch((error) => {
                    setSubmitting(false);
                    handleError(error);
                });
        },
        [email, id, poster],
    );

    const handleChangeEmail = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.target.value);
        },
        [setEmail],
    );

    return (
        <div className="container">
            <div className="minContainer">
                <div>
                    <div className="mb-4 text-center">
                        <h3>Forgot Password</h3>
                    </div>
                    <div>
                        <Form onSubmit={onSubmit}>
                            <Form.Group controlId="email">
                                <Form.Label>Email Address</Form.Label>
                                <NavLink onClick={goBack} className="float-right" tabIndex={-1}>
                                    Back to Login
                                </NavLink>
                                <Form.Control
                                    onChange={handleChangeEmail}
                                    name={"email"}
                                    type="email"
                                    placeholder="Enter email"
                                    disabled={isSubmitting}
                                    autoComplete="email"
                                    value={email}
                                />
                            </Form.Group>
                            <Button
                                variant="primary"
                                className="mb-2 mt-3 w-100"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                Reset Password
                            </Button>
                        </Form>
                    </div>
                </div>
                <div className="mt-2">
                    <a href="https://www.lobbie.com" target="_blank">
                        <img
                            className="login-logo mt-1"
                            src="/powered_by_lobbie.svg"
                            alt="Lobbie Logo"
                        />
                    </a>
                </div>
                <div style={{ textAlign: "center" }}>
                    <LobbieSpinner isHidden={!isSubmitting} />
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
